/**
 * useTrialStatus — polls /api/trial/status periodically and exposes:
 *   { status, loading, error, isPaid, isTrial, isExpired, refetch }
 *
 * `status` shape mirrors the backend ``compute_trial_status`` response:
 *   { state, trial_started_at, trial_ends_at, seconds_remaining,
 *     hours_remaining, days_remaining, is_paid, is_trial, is_expired,
 *     trial_duration_days }
 *
 * Polling cadence:
 *   - active trial w/ >24h left → 5 min
 *   - active trial w/ <24h left → 1 min (urgency display)
 *   - expired or paid           → no polling
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { setUserContext } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function useTrialStatus({ enabled = true } = {}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/trial/status`, { withCredentials: true });
      setStatus(r.data);
      setError(null);
      // dataLayer — push trial_state so GTM audiences can segment by lifecycle
      setUserContext({ trial_state: r.data?.state || 'none' });
      return r.data;
    } catch (e) {
      // 401 = anonymous user — silently return null (not an error per se)
      if (e?.response?.status === 401) {
        setStatus(null);
        setError(null);
      } else {
        setError(e);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      const data = await fetchStatus();
      if (cancelled || !data) return;
      // Schedule a repeat poll based on urgency
      const scheduleNext = (current) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (!current) return;
        if (current.state === 'paid' || current.state === 'trial_expired') return;
        const intervalMs = current.hours_remaining <= 24 ? 60_000 : 5 * 60_000;
        timerRef.current = setTimeout(async () => {
          const next = await fetchStatus();
          if (!cancelled) scheduleNext(next);
        }, intervalMs);
      };
      scheduleNext(data);
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, fetchStatus]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
    isPaid: !!status?.is_paid,
    isTrial: !!status?.is_trial,
    isExpired: !!status?.is_expired,
  };
}
